<template>
  <div class="customer-boolean">
    <button
      type="button"
      class="customer-switch"
      role="switch"
      :aria-label="label"
      :aria-checked="value"
      :aria-busy="saving"
      :disabled="saving"
      @click="save"
    >
      <span class="customer-switch-thumb" />
    </button>
    <span role="status" class="customer-switch-status">{{ saving ? 'Saving…' : saved ? 'Saved' : '' }}</span>
    <p v-if="error" role="alert" class="text-danger">Could not save. Setting restored. Try again.</p>
  </div>
</template>
<script setup lang="ts">
import type { CustomerBooleanSettingProps, RequestCompany } from '@/types/Models/Company'
import { ref, watch } from 'vue'
import { useCustomersStore } from '@/store/customers'
import CompanyProxy from '@/proxies/CompanyProxy'
const props = defineProps<CustomerBooleanSettingProps>()
const customers = useCustomersStore()
const value = ref(!!props.company[props.field])
const saving = ref(false)
const error = ref(false)
const saved = ref(false)
watch(
  () => props.company[props.field],
  (newValue) => {
    value.value = !!newValue
  },
)
async function save() {
  if (saving.value) return
  const previousValue = value.value
  value.value = !previousValue
  saved.value = false
  saving.value = true
  error.value = false
  try {
    const data: Partial<RequestCompany> = { [props.field]: value.value }
    if (props.field === 'isBufferingEnabled') {
      data.bufferingOptions = value.value
        ? props.company.bufferingOptions || { cutoffTime: null, itemsCount: null, minutesDiff: 15 }
        : null
    }
    await new CompanyProxy().update(props.company.id, data)
    const customer = customers.listCustomers?.find((item) => item.id === props.company.id)
    if (customer) {
      customer[props.field] = value.value
      if (props.field === 'isBufferingEnabled') customer.bufferingOptions = data.bufferingOptions ?? null
    }
    saved.value = true
  } catch {
    value.value = previousValue
    error.value = true
  } finally {
    saving.value = false
  }
}
</script>
<style scoped>
.customer-boolean {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  max-width: 280px;
}
.customer-switch {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  width: 52px;
  height: 32px;
  padding: 3px;
  border: 0;
  border-radius: 999px;
  background: #717b7a;
  cursor: pointer;
  transition: background-color 150ms ease;
}
.customer-switch[aria-checked='true'] {
  background: #008b83;
}
.customer-switch-thumb {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px #0003;
  transition: transform 150ms ease;
}
.customer-switch[aria-checked='true'] .customer-switch-thumb {
  transform: translateX(20px);
}
.customer-switch:focus-visible {
  outline: 3px solid #008b83;
  outline-offset: 3px;
}
.customer-switch:disabled {
  cursor: wait;
  opacity: 0.65;
}
.customer-switch-status {
  font-size: 12px;
  color: #526360;
}
.customer-switch-status:empty {
  display: none;
}
p {
  flex-basis: 100%;
  font-size: 12px;
  margin: 0;
}
@media (prefers-reduced-motion: reduce) {
  .customer-switch,
  .customer-switch-thumb {
    transition: none;
  }
}
</style>
